import { StatusesFilters } from "./ItemsReport";
import { ItemReportDataExpanded } from "./itemsReportApi";

export type ItemReportDataAggregated = ItemReportDataExpanded
  & {
    relatedProjects:
    Pick<ItemReportDataExpanded, 'projectId'
      | 'projectName'
      | 'jobType'
      | 'sold'
      | 'isApproved'
      | 'isOnHold'
      | 'lost'
      | 'workareaName'
      | 'estimatedQuantity'
      | 'actualQuantity'
      | 'customerDisplayName'
    >[]
  };

export const aggregateItemsData = (items: ItemReportDataExpanded[]): ItemReportDataAggregated[] => {
  const aggregatedItems: { [key: string]: ItemReportDataExpanded & { relatedProjects: any[], projectWorkareaSet: Set<string> } } = {};

  items.forEach(item => {
    const key = `${item.name}|${item.units}`;
    const projectWorkareaKey = `${item.projectId}|${item.workareaName}`;
    if (!aggregatedItems[key]) {
      aggregatedItems[key] = {
        ...item,
        relatedProjects: [{
          projectId: item.projectId,
          projectName: item.projectName,
          jobType: item.jobType,
          sold: item.sold,
          isApproved: item.isApproved,
          isOnHold: item.isOnHold,
          lost: item.lost,
          workareaName: item.workareaName,
          estimatedQuantity: item.estimatedQuantity,
          actualQuantity: item.actualQuantity,
          customerDisplayName: item.customerDisplayName
        }],
        projectWorkareaSet: new Set([projectWorkareaKey])
      };
    } else {
      aggregatedItems[key].estimatedQuantity += item.estimatedQuantity;
      aggregatedItems[key].actualQuantity += item.actualQuantity;
      if (!aggregatedItems[key].projectWorkareaSet.has(projectWorkareaKey)) {
        aggregatedItems[key].relatedProjects.push({
          projectId: item.projectId,
          projectName: item.projectName,
          jobType: item.jobType,
          sold: item.sold,
          isApproved: item.isApproved,
          isOnHold: item.isOnHold,
          lost: item.lost,
          workareaName: item.workareaName,
          estimatedQuantity: item.estimatedQuantity,
          actualQuantity: item.actualQuantity,
          customerDisplayName: item.customerDisplayName
        });
        aggregatedItems[key].projectWorkareaSet.add(projectWorkareaKey);
      }
    }
  });

  return Object.values(aggregatedItems).map(item => {
    const { projectWorkareaSet, ...rest } = item;
    return rest;
  }) as ItemReportDataAggregated[];
};

export const getStatusTagColor = (status: string): string => {
  switch (status) {
    case 'isApproved':
      return 'green';
    case 'lost':
      return 'red';
    default:
      return 'default';
  }
}

export const getStatusTagName = (status: string) => {
  switch (status) {
    case 'isApproved':
      return 'Approved';
    case 'isOnHold':
      return 'On Hold';
    case 'lost':
      return 'Lost';
    default:
      return 'Draft';
  }
}

export const getFilteredItems = (items: ItemReportDataAggregated[], searchTerm: string, statusSet: Set<StatusesFilters>, jobTypeSet: Set<number>): ItemReportDataAggregated[] => {
  return items
    .map(item => {
      const filteredRelatedProjects = item.relatedProjects.filter(project =>
        (statusSet.size === 0 || 
          (project.isApproved && statusSet.has('isApproved')) ||
          (project.isOnHold && statusSet.has('isOnHold')) ||
          (project.lost && statusSet.has('lost')) ||
          (statusSet.has('draft') && !project.isApproved && !project.isOnHold && !project.lost)
        ) && 
        (jobTypeSet.size === 0 || jobTypeSet.has(project.jobType))
      );

      const matchesSearchTerm = item.name.toLowerCase().includes(searchTerm)

      // Recalculate quantities based on filtered projects
      const estimatedQuantity = filteredRelatedProjects.reduce((sum, project) => sum + project.estimatedQuantity, 0);
      const actualQuantity = filteredRelatedProjects.reduce((sum, project) => sum + project.actualQuantity, 0);

      return {
        ...item,
        relatedProjects: filteredRelatedProjects,
        matchesSearchTerm,
        estimatedQuantity,
        actualQuantity
      };
    })
    .filter(item => {
      if (!statusSet.size && !jobTypeSet.size) return item.matchesSearchTerm;
      return item.matchesSearchTerm && item.relatedProjects.length > 0;
    })
    .map(({ matchesSearchTerm, ...item }) => item) as ItemReportDataAggregated[];
};

export const getJobTypes = (items: ItemReportDataAggregated[]): number[] => {
  const jobTypes = new Set<number>();
  items.forEach(item => {
    jobTypes.add(item.jobType);
    item.relatedProjects.forEach(project => jobTypes.add(project.jobType));
  });

  return jobTypes.size ? Array.from(jobTypes) : [];
}